import { useState } from "react";
import api from "../lib/api";
import { ArrowRight, ShieldAlert, Phone } from "lucide-react";
import { toast } from "sonner";

const SITUATIONS = [
  { v: "active-criminal", l: "Active Federal/State Criminal Case" },
  { v: "investigation", l: "Under Investigation · Pre-Indictment" },
  { v: "section-1983", l: "§1983 Civil Rights Claim" },
  { v: "first-amendment", l: "First Amendment / Retaliation" },
  { v: "post-conviction", l: "Post-Conviction (§2255, Habeas, Brady)" },
];

export default function DefendersIntake() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", jurisdiction: "",
    situation_type: "active-criminal", description: "", urgency: "this-week", in_custody: false,
  });
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/defenders/client-intake", form);
      setResult(data);
      toast.success("Intake received.");
    } catch { toast.error("Submission failed."); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="defenders-intake">
      <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// Client Intake</div>
      <h1 className="display-black text-4xl lg:text-5xl uppercase text-[#064E3B] mt-3 tracking-tighter">Hire a fighter.</h1>
      <p className="text-gray-700 mt-3">We route you to the right network member within 24 hours.</p>

      <div className="mt-6 border border-[#D97706] bg-red-50 p-5 flex items-start gap-3">
        <Phone className="w-5 h-5 text-[#D97706] mt-0.5 shrink-0" />
        <div className="text-sm text-gray-900">
          <span className="font-bold">In custody or facing imminent court action?</span> Call <a href="tel:9184073395" className="mono font-bold text-[#D97706] hover:underline">918-407-3395</a> immediately. The intake form is for non-emergency routing.
        </div>
      </div>

      {result ? (
        <div className="mt-10 border border-[#064E3B] bg-gray-50 p-10" data-testid="intake-success">
          <ShieldAlert className="w-10 h-10 text-[#D97706]" />
          <h2 className="display-black text-2xl uppercase text-[#064E3B] mt-4">Intake received.</h2>
          <p className="text-gray-800 mt-3">{result.message}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 border border-gray-300 p-7" data-testid="defenders-intake-form">
          <input className="input-federal md:col-span-2" required placeholder="Your full legal name" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} data-testid="di-name" />
          <input className="input-federal" required type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="di-email" />
          <input className="input-federal" required placeholder="Phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="di-phone" />
          <input className="input-federal md:col-span-2" required placeholder="Jurisdiction (city, state, federal district)" value={form.jurisdiction} onChange={e => setForm({...form, jurisdiction: e.target.value})} data-testid="di-jurisdiction" />

          <select className="input-federal" value={form.situation_type} onChange={e => setForm({...form, situation_type: e.target.value})} data-testid="di-situation">
            {SITUATIONS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          <select className="input-federal" value={form.urgency} onChange={e => setForm({...form, urgency: e.target.value})} data-testid="di-urgency">
            <option value="immediate">Immediate (this week)</option>
            <option value="this-week">This week</option>
            <option value="this-month">This month</option>
            <option value="consult">General consult</option>
          </select>

          <textarea className="input-federal md:col-span-2" rows={6} required placeholder="What's happening. Charges (if any). Dates. Names of agencies and prosecutors. Be specific." value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="di-description" />

          <label className="md:col-span-2 flex items-center gap-3 text-sm text-gray-900" data-testid="di-custody">
            <input type="checkbox" checked={form.in_custody} onChange={e => setForm({...form, in_custody: e.target.checked})} className="w-4 h-4" />
            I (or the person I am submitting for) am currently in custody.
          </label>

          <button type="submit" className="btn-signal md:col-span-2 justify-center" data-testid="di-submit">Send to Network <ArrowRight className="w-4 h-4" /></button>
        </form>
      )}
    </div>
  );
}
